import React from 'react';
import { Code, Smartphone, Building2, ShoppingCart, Layers, Boxes, ShieldCheck, Users, ArrowUpRight, CheckCircle2, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

const Services = () => {
  const services = [
    {
      icon: Code,
      title: 'Website & Software Development',
      description: 'Custom websites, enterprise software, and web applications built for high performance, top-tier security, and scalability.',
      features: [
        'Website Development & Redesign',
        'Enterprise Software Development',
        'Custom Web Applications',
        'CRM & ERP Consulting',
        'API Development & Integration'
      ],
      link: '/web-development',
      badge: 'Core Solution',
      borderGradient: 'from-cyan-400 via-sky-500 via-blue-600 to-cyan-400',
      iconColor: 'from-blue-600 to-cyan-500',
      shadowColor: 'shadow-cyan-500/15'
    },
    {
      icon: Smartphone,
      title: 'Mobile App Development',
      description: 'From concept to App Store deployment, we craft native and cross-platform mobile apps for iOS and Android.',
      features: [
        'Android & iOS Native Apps',
        'Cross-Platform (React Native / Flutter)',
        'Mobile UI/UX Design Systems',
        'App Store & Play Store Deployment',
        'Backend System Integration'
      ],
      link: '/mobile-app-development',
      badge: 'High Impact',
      borderGradient: 'from-amber-400 via-orange-500 via-red-500 to-amber-400',
      iconColor: 'from-orange-500 to-amber-500',
      shadowColor: 'shadow-orange-500/15'
    },
    {
      icon: Building2,
      title: 'Industries Web Applications',
      description: 'Tailored enterprise web applications engineered specifically for Healthcare, Finance, E-commerce, Logistics, and Manufacturing.',
      features: [
        'Healthcare & Telemedicine Portals',
        'E-commerce & Multi-Vendor Marketplaces',
        'Fintech & Banking Solutions',
        'Logistics & Fleet Supply Chain ERP',
        'Real Estate & Education Platforms'
      ],
      link: '/enterprise-industry',
      badge: 'Industry Focused',
      borderGradient: 'from-purple-400 via-indigo-500 via-blue-500 to-purple-400',
      iconColor: 'from-purple-600 to-indigo-600',
      shadowColor: 'shadow-purple-500/15'
    },
    {
      icon: ShoppingCart,
      title: 'E-commerce Solutions',
      description: 'Build engaging, secure, and scalable online storefronts designed to drive higher conversions and customer loyalty.',
      features: [
        'eCommerce Platform Consulting',
        'Custom Storefront & Marketplace Dev',
        'Responsive Mobile-First UI/UX',
        'Payment Gateway Integration',
        'Inventory & ERP Sync'
      ],
      link: '/ecommerce-solutions',
      badge: 'Conversion Focused',
      borderGradient: 'from-emerald-400 via-teal-500 via-cyan-500 to-emerald-400',
      iconColor: 'from-teal-600 to-emerald-500',
      shadowColor: 'shadow-emerald-500/15'
    },
    {
      icon: Layers,
      title: 'CMS & Portal Solutions',
      description: 'Empower marketing teams with custom WordPress, Drupal, Strapi, and Headless CMS platforms for rapid content publishing.',
      features: [
        'WordPress Custom Themes & Plugins',
        'Enterprise Drupal Multi-Site Portals',
        'Strapi & Headless CMS APIs',
        'Webflow Bespoke Design & Hosting',
        'Zero-Downtime Platform Migration'
      ],
      link: '/cms-solution',
      badge: 'Content Velocity',
      borderGradient: 'from-pink-400 via-rose-500 via-orange-400 to-pink-400',
      iconColor: 'from-pink-600 to-rose-500',
      shadowColor: 'shadow-pink-500/15'
    },
    {
      icon: Boxes,
      title: 'Software Product Engineering',
      description: 'Full-cycle engineering guiding your software product from early MVP prototypes to global live deployment.',
      features: [
        'MVP Rapid Development',
        'Progressive Web Apps (PWAs)',
        'UI/UX Design Systems',
        'Legacy Modernization',
        'Application Maintenance'
      ],
      link: '/enterprise-solutions',
      badge: 'Full Cycle',
      borderGradient: 'from-blue-400 via-cyan-500 via-teal-400 to-blue-400',
      iconColor: 'from-cyan-600 to-blue-600',
      shadowColor: 'shadow-cyan-500/15'
    },
    {
      icon: Users,
      title: 'Dedicated Teams & Staff Augmentation',
      description: 'On-demand access to top-tier developers and engineers to scale your in-house product velocity seamlessly.',
      features: [
        'Offshore & Nearshore Teams',
        'Dedicated Senior Developers',
        'Flexible Staff Augmentation',
        'Seamless Agile Integration',
        'Direct Project Management'
      ],
      link: '/contact',
      badge: 'Scale Fast',
      borderGradient: 'from-violet-400 via-purple-500 via-indigo-500 to-violet-400',
      iconColor: 'from-indigo-600 to-purple-600',
      shadowColor: 'shadow-purple-500/15'
    },
    {
      icon: ShieldCheck,
      title: 'Quality Assurance & Testing',
      description: 'Comprehensive QA services guaranteeing reliability, speed, and security across all digital deliverables.',
      features: [
        'QA Strategy & Architecture',
        'Automated & Manual Testing',
        'Mobile App QA',
        'Security & Compliance Audits',
        'Performance Optimization'
      ],
      link: '/qa-testing',
      badge: 'Zero Defect',
      borderGradient: 'from-sky-400 via-blue-500 via-indigo-500 to-sky-400',
      iconColor: 'from-sky-600 to-blue-600',
      shadowColor: 'shadow-sky-500/15'
    }
  ];

  return (
    <section id="services" className="py-24 bg-gray-900 relative overflow-hidden text-left">
      <div className="container mx-auto px-6 relative z-10">

        {/* Section Header */}
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <div className="inline-block bg-cyan-500/10 border border-cyan-500/20 rounded-full px-4 py-1.5 mb-4">
            <span className="text-cyan-400 text-sm font-semibold uppercase tracking-wider">
              Advanced Digital Solutions
            </span>
          </div>
          <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-4 leading-tight">
            End-to-End Digital Services to <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-orange-400 bg-clip-text text-transparent">Design, Build & Scale</span>
          </h2>
          <p className="text-gray-300 text-base md:text-lg leading-relaxed">
            Whether launching a new venture or scaling an enterprise globally, we build robust digital ecosystems starting with high-performing websites and extending into custom web apps and mobile solutions.
          </p>
        </div>

        {/* Services Grid with Spectrum Motion Borders & White Interiors */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {services.map((service, index) => (
            <div
              key={index}
              className={`p-[2px] rounded-2xl bg-gradient-to-r ${service.borderGradient} animate-gradient-border shadow-xl ${service.shadowColor} hover:scale-[1.03] transition-all duration-300 flex flex-col group`}
            >
              <div className="bg-white rounded-[14px] p-7 h-full flex flex-col justify-between text-left">
                <div>
                  {/* Header Row */}
                  <div className="flex items-center justify-between mb-5">
                    <div className={`w-14 h-14 bg-gradient-to-br ${service.iconColor} rounded-xl flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-300`}>
                      <service.icon className="w-7 h-7 text-white" />
                    </div>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-cyan-800 bg-cyan-50 border border-cyan-200 px-3 py-1 rounded-full">
                      {service.badge}
                    </span>
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-extrabold text-slate-900 mb-3 group-hover:text-cyan-600 transition-colors leading-snug">
                    {service.title}
                  </h3>

                  {/* Description */}
                  <p className="text-slate-600 text-sm mb-6 leading-relaxed">
                    {service.description}
                  </p>

                  {/* Feature List */}
                  <ul className="space-y-2.5 mb-6">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-xs md:text-sm text-slate-700 font-medium">
                        <CheckCircle2 className="w-4 h-4 text-cyan-600 mr-2 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Bottom CTA Button */}
                <a
                  href={service.link}
                  className="w-full py-3 px-4 rounded-xl bg-slate-900 hover:bg-gradient-to-r hover:from-cyan-500 hover:to-blue-600 text-white font-bold text-xs md:text-sm flex items-center justify-between transition-all duration-300 shadow-sm group-hover:shadow-md"
                >
                  <span>Explore Solution</span>
                  <ArrowUpRight className="w-4 h-4 text-cyan-400 group-hover:text-white group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* Global CTA Box with Light Theme & Running Motion Animated Background */}
        <div className="relative rounded-3xl overflow-hidden bg-slate-50 border border-gray-200 shadow-xl p-8 md:p-14 group">
          {/* Running Motion Animated Mesh & Background Motion Orbs */}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#cbd5e180_1px,transparent_1px),linear-gradient(to_bottom,#cbd5e180_1px,transparent_1px)] bg-[size:3rem_3rem] pointer-events-none animate-pulse opacity-70" />
          <div className="absolute -top-10 -left-10 w-96 h-96 bg-gradient-to-tr from-cyan-300/35 via-blue-200/35 to-transparent rounded-full blur-3xl pointer-events-none animate-[ping_8s_ease-in-out_infinite]" />
          <div className="absolute -bottom-10 -right-10 w-96 h-96 bg-gradient-to-br from-orange-300/35 via-amber-200/35 to-transparent rounded-full blur-3xl pointer-events-none animate-[pulse_6s_ease-in-out_infinite]" />

          {/* Content Box */}
          <div className="relative z-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div className="text-left max-w-2xl">
              <div className="inline-flex items-center space-x-2 bg-cyan-100/90 border border-cyan-300 rounded-full px-3.5 py-1 mb-4 shadow-sm">
                <span className="text-cyan-800 text-sm font-mono font-bold uppercase tracking-wider">
                  EXECUTION-READY ARCHITECTURE
                </span>
              </div>

              <h3 className="text-3xl md:text-4xl font-black text-gray-900 mb-4 leading-tight tracking-tight">
                Start Your Digital <span className="bg-gradient-to-r from-orange-600 via-amber-600 to-cyan-600 bg-clip-text text-transparent">Transformation Journey</span> Today
              </h3>

              <p className="text-gray-600 text-base leading-relaxed mb-6 font-normal">
                Talk to our senior technical architects to define your enterprise software roadmap, select optimal technology stacks, and execute with zero tech debt.
              </p>

              <div className="flex flex-wrap gap-4 text-sm font-mono text-gray-700">
                <span className="flex items-center gap-1.5 bg-white border border-gray-200 px-3.5 py-2 rounded-xl shadow-sm">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  Free 45-Min Architecture Audit
                </span>
                <span className="flex items-center gap-1.5 bg-white border border-gray-200 px-3.5 py-2 rounded-xl shadow-sm">
                  <CheckCircle2 className="w-4 h-4 text-orange-600" />
                  Sub-Second PageSpeed Guarantee
                </span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row lg:flex-col gap-4 w-full lg:w-auto flex-shrink-0">
              <a href="/contact">
                <Button className="w-full bg-gradient-to-r from-orange-500 via-amber-500 to-cyan-600 hover:from-orange-600 hover:to-cyan-700 text-white px-8 py-6 text-base font-extrabold rounded-xl shadow-lg shadow-orange-500/20 transition-all duration-300 flex items-center justify-center gap-2">
                  Consult Senior Architects
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </a>
            </div>
          </div>
        </div>

      </div>

      {/* Decorative Glow */}
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />
    </section>
  );
};

export default Services;